/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  accessAPI,
  downloadFile,
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  CampoInformacion,
  ArchivoPreview,
  PdfInline,
  SubidorArchivos,
  formatFecha,
  useIsMobile,
  urlBaseAPI,
  tituloBase,
  TablaFiltrable
} from "../../../shared";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Image } from "primereact/image";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { Menu } from "primereact/menu";
import { useContextoAsistente } from "../../asistente/ContextoAsistente";

const API_URL = urlBaseAPI();

export default function Muestra() {
  const { muestraId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const esXl = !useIsMobile(1199);
  const esMobile = useIsMobile();
  const [muestra, setMuestra] = useState(null);
  useContextoAsistente({
    tipo: "muestra",
    id: muestraId,
    nombre: muestra ? `Muestra ${muestra.numero}` : undefined
  });
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [loaderRetiro, setLoaderRetiro] = useState(false);
  const [loaderDesactivar, setLoaderDesactivar] = useState(false);
  const [dialogQRVisible, setDialogQRVisible] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [loaderQR, setLoaderQR] = useState(false);
  const [sidebarCandidatos, setSidebarCandidatos] = useState(false);
  const [candidatos, setCandidatos] = useState(null);
  const [loaderCandidatos, setLoaderCandidatos] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  // Actividades del candidato elegido en el formulario de vinculación, y el
  // proceso elegido, para rearmar el segundo dropdown cuando cambia el primero.
  const [procesoBorrador, setProcesoBorrador] = useState(null);
  const [actividadesCandidato, setActividadesCandidato] = useState([]);
  const [formVincularKey, setFormVincularKey] = useState(0);
  const [formEnsayoKey, setFormEnsayoKey] = useState(0);
  const [desvinculandoId, setDesvinculandoId] = useState(null);
  const [ensayos, setEnsayos] = useState(null);
  const [sidebarEnsayo, setSidebarEnsayo] = useState(false);
  const [guardandoEnsayo, setGuardandoEnsayo] = useState(false);
  const [actividadesOpciones, setActividadesOpciones] = useState([]);
  const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
  const [normasOpciones, setNormasOpciones] = useState([]);
  const [loaderNormas, setLoaderNormas] = useState(false);
  const [normaSeleccionada, setNormaSeleccionada] = useState(null);
  const [puntosNorma, setPuntosNorma] = useState([]);
  const [loaderPuntos, setLoaderPuntos] = useState(false);
  const [puntosSeleccionados, setPuntosSeleccionados] = useState([]);
  const [tipoEnsayoSel, setTipoEnsayoSel] = useState("SEGUIMIENTO");
  // Número tipeado de la etiqueta de ensayo impresa (solo en el alta: tiene
  // que corresponder a un ensayo vacío del lote).
  const [numeroEnsayoSel, setNumeroEnsayoSel] = useState(null);
  const [notasEnsayoTexto, setNotasEnsayoTexto] = useState("");
  const [notasEtiquetaTexto, setNotasEtiquetaTexto] = useState("");
  const [imprimiendoEnsayoId, setImprimiendoEnsayoId] = useState(null);
  const [ensayoEditandoId, setEnsayoEditandoId] = useState(null);
  const menuAccionesRef = useRef(null);
  const [menuAccionesItems, setMenuAccionesItems] = useState([]);
  // Evita reabrir el sidebar de ensayo si el efecto de abajo corre de nuevo
  // (p. ej. porque cargar() refresca la muestra).
  const registroEnsayoAutoRef = useRef(false);
  const toast = useRef(null);

  useEffect(() => {
    cargar();
    cargarEnsayos();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muestraId]);

  // Llegada desde el listado de ensayos: la lista navega hasta acá con el
  // número del ensayo pendiente en el state, y esta ficha abre sola el sidebar
  // de registro con ese número precargado. El state se limpia del historial
  // para que un refresh no lo reabra.
  useEffect(() => {
    if (!muestra || registroEnsayoAutoRef.current) return;
    const numeroPre = location.state?.registrarEnsayoNumero;
    if (!numeroPre) return;
    registroEnsayoAutoRef.current = true;
    window.history.replaceState({}, "");
    abrirSidebarEnsayo();
    setNumeroEnsayoSel(numeroPre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muestra]);

  function cargarEnsayos() {
    accessAPI(
      "GET",
      `admin/ensayo/muestra/${muestraId}`,
      null,
      (resp) => setEnsayos(resp),
      (resp) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al cargar los ensayos",
          sticky: true,
        });
      },
    );
  }

  function cargar() {
    accessAPI(
      "GET",
      `admin/muestra/${muestraId}`,
      null,
      (resp) => {
        setMuestra(resp);
        document.title = `Muestra ${resp.numero}`;
      },
      (resp) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al cargar muestra",
          sticky: true,
        });
      },
    );
  }

  function guardar(campos) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/muestra/${muestraId}`,
      campos,
      () => {
        setLoaderEditar(false);
        setSidebarEditar(false);
        cargar();
      },
      (resp) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al guardar",
          sticky: true,
        });
      },
    );
  }

  function eliminarComprobante() {
    confirmDialog({
      message: "¿Está seguro que desea eliminar el comprobante de compra?",
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/muestra/${muestraId}/comprobante`,
          null,
          () => cargar(),
          (resp) =>
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: resp[0]?.msg || "Error al eliminar",
              sticky: true,
            }),
        );
      },
    });
  }

  function eliminarImagen(imagenId) {
    confirmDialog({
      message: "¿Está seguro que desea eliminar esta imagen?",
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/muestra/${muestraId}/imagen/${imagenId}`,
          null,
          () => cargar(),
          (resp) =>
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: resp[0]?.msg || "Error al eliminar",
              sticky: true,
            }),
        );
      },
    });
  }

  function retirar() {
    confirmDialog({
      message: "¿Está seguro que desea marcar esta muestra como retirada de la caja?",
      header: "Confirmar retiro",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Retirar",
      rejectLabel: "Cancelar",
      accept: () => {
        setLoaderRetiro(true);
        accessAPI(
          "POST",
          `admin/muestra/${muestraId}/retiro`,
          {},
          () => {
            setLoaderRetiro(false);
            cargar();
          },
          (resp) => {
            setLoaderRetiro(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: resp[0]?.msg || "Error al retirar",
              sticky: true,
            });
          },
        );
      },
    });
  }

  function devolver() {
    confirmDialog({
      message: "¿Está seguro que desea marcar esta muestra como devuelta a la caja?",
      header: "Confirmar devolución",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Devolver",
      rejectLabel: "Cancelar",
      accept: () => {
        setLoaderRetiro(true);
        accessAPI(
          "POST",
          `admin/muestra/${muestraId}/devolucion`,
          {},
          () => {
            setLoaderRetiro(false);
            cargar();
          },
          (resp) => {
            setLoaderRetiro(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: resp[0]?.msg || "Error al devolver",
              sticky: true,
            });
          },
        );
      },
    });
  }

  function desactivar() {
    confirmDialog({
      message:
        "¿Está seguro que desea desactivar esta muestra? La muestra dejará de estar bajo control del organismo y será desasignada de su caja, pero se conserva el registro.",
      header: "Confirmar desactivación",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Desactivar",
      rejectLabel: "Cancelar",
      accept: () => {
        setLoaderDesactivar(true);
        accessAPI(
          "POST",
          `admin/muestra/${muestraId}/desactivar`,
          {},
          () => {
            setLoaderDesactivar(false);
            cargar();
          },
          (resp) => {
            setLoaderDesactivar(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: resp[0]?.msg || "Error al desactivar",
              sticky: true,
            });
          },
        );
      },
    });
  }

  function abrirDialogQR() {
    setDialogQRVisible(true);
    setLoaderQR(true);
    setUploadInfo(null);
    accessAPI(
      "GET",
      `admin/muestra/${muestraId}/upload-info`,
      null,
      (resp) => {
        setUploadInfo(resp);
        setLoaderQR(false);
      },
      (resp) => {
        setLoaderQR(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al generar el QR",
          sticky: true,
        });
      },
    );
  }

  function activar() {
    setLoaderDesactivar(true);
    accessAPI(
      "POST",
      `admin/muestra/${muestraId}/activar`,
      {},
      () => {
        setLoaderDesactivar(false);
        cargar();
      },
      (resp) => {
        setLoaderDesactivar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al activar",
          sticky: true,
        });
      },
    );
  }

  function abrirCandidatos() {
    setSidebarCandidatos(true);
    setLoaderCandidatos(true);
    setCandidatos(null);
    setProcesoBorrador(null);
    setActividadesCandidato([]);
    setFormVincularKey((k) => k + 1);
    accessAPI(
      "GET",
      `admin/muestra/${muestraId}/procesos-candidatos`,
      null,
      (resp) => {
        setCandidatos(resp);
        setLoaderCandidatos(false);
      },
      (resp) => {
        setLoaderCandidatos(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al buscar procesos candidatos",
          sticky: true,
        });
      },
    );
  }

  function vincular(campos) {
    if (!campos.procesoId) return;
    setVinculando(true);
    accessAPI(
      "POST",
      `admin/muestra/${muestraId}/vincular-proceso`,
      { procesoId: campos.procesoId, actividadId: campos.actividadId || null },
      () => {
        setVinculando(false);
        setSidebarCandidatos(false);
        cargar();
      },
      (resp) => {
        setVinculando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al vincular el proceso",
          sticky: true,
        });
      },
    );
  }

  // Cuando cambia el proceso elegido en el formulario, se cargan sus
  // actividades para el segundo dropdown (de la más nueva a la más vieja).
  function cambioFormVincular(campos) {
    const procesoId = campos.procesoId || null;
    if (procesoId === procesoBorrador) return;
    setProcesoBorrador(procesoId);
    setActividadesCandidato([]);
    if (!procesoId) {
      setFormVincularKey((k) => k + 1);
      return;
    }
    accessAPI(
      "GET",
      `admin/muestra/proceso/${procesoId}/actividades`,
      null,
      (resp) => {
        setActividadesCandidato(resp);
        setFormVincularKey((k) => k + 1);
      },
      () => {
        setActividadesCandidato([]);
        setFormVincularKey((k) => k + 1);
      },
    );
  }

  function desvincular(procesoId) {
    setDesvinculandoId(procesoId);
    accessAPI(
      "DELETE",
      `admin/muestra/${muestraId}/vincular-proceso/${procesoId}`,
      null,
      () => {
        setDesvinculandoId(null);
        cargar();
      },
      (resp) => {
        setDesvinculandoId(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al desvincular el proceso",
          sticky: true,
        });
      },
    );
  }

  // Tipos de ensayo — el enum interno es en mayúscula; acá el label que ve el usuario.
  const TIPOS_ENSAYO = [
    { value: "COMPLETO", etiqueta: "Completo" },
    { value: "SEGUIMIENTO", etiqueta: "Seguimiento" },
    { value: "ESPECIFICO", etiqueta: "Específico" },
  ];

  // Qué puntos muestra el sidebar según el tipo:
  //  SEGUIMIENTO → solo los marcados como seguimiento · COMPLETO/ESPECIFICO → todos.
  // Selección inicial según el tipo:
  //  COMPLETO → todos · SEGUIMIENTO → los de seguimiento · ESPECIFICO → ninguno.
  function seleccionInicialPorTipo(tipo, puntos) {
    if (tipo === "COMPLETO") return puntos.map((p) => p.texto);
    if (tipo === "SEGUIMIENTO") return puntos.filter((p) => p.seguimiento).map((p) => p.texto);
    return [];
  }

  // Las actividades a las que puede corresponder un ensayo: la nativa de la
  // muestra primero, y las señaladas por los vínculos. `actividadExtra` cubre
  // la edición de un ensayo cuyo vínculo se borró después: sigue apareciendo
  // para no romper el formulario.
  function actividadesDisponibles(actividadExtra) {
    const porId = new Map();

    const etiquetaDe = (a, origen) => {
      const fecha = a.fechacreacion ? formatFecha(a.fechacreacion) : "";
      return `${a.actividadtipo?.nombre || "Actividad"} — ${fecha}${origen ? ` (${origen})` : ""}`;
    };

    if (muestra?.actividad?.id) {
      porId.set(muestra.actividad.id, {
        value: muestra.actividad.id,
        procesoid: muestra.actividad.procesoid,
        etiqueta: etiquetaDe(muestra.actividad, "actividad de la muestra"),
      });
    }
    for (const v of muestra?.muestra_x_proceso || []) {
      if (v.actividad?.id && !porId.has(v.actividad.id)) {
        porId.set(v.actividad.id, {
          value: v.actividad.id,
          procesoid: v.procesoid,
          etiqueta: etiquetaDe(v.actividad, v.proceso?.nombre || null),
        });
      }
    }
    if (actividadExtra?.id && !porId.has(actividadExtra.id)) {
      porId.set(actividadExtra.id, {
        value: actividadExtra.id,
        procesoid: actividadExtra.procesoid,
        etiqueta: etiquetaDe(actividadExtra, null),
      });
    }
    return [...porId.values()];
  }

  function abrirSidebarEnsayo() {
    setEnsayoEditandoId(null);
    setSidebarEnsayo(true);
    setNormasOpciones([]);
    setNormaSeleccionada(null);
    setPuntosNorma([]);
    setPuntosSeleccionados([]);
    setTipoEnsayoSel("SEGUIMIENTO");
    setNumeroEnsayoSel(null);
    setNotasEnsayoTexto("");
    setNotasEtiquetaTexto("");

    const opciones = actividadesDisponibles();
    setActividadesOpciones(opciones);

    // Se preselecciona solo cuando no hay ambigüedad: una única actividad.
    const unica = opciones.length === 1 ? opciones[0] : null;
    setActividadSeleccionada(unica?.value ?? null);
    setFormEnsayoKey((k) => k + 1);
    if (unica?.procesoid) cargarNormas(unica.procesoid);
  }

  // El formulario del ensayo tiene tres dropdowns encadenados: actividad →
  // normas → puntos. Cada cambio de un eslabón recarga el siguiente y rearma
  // el formulario; lo tipeado (notas, puntos) se sincroniza siempre para que
  // el rearmado no lo pierda.
  function cambioFormEnsayo(campos) {
    // REGLA: solo se reacciona a campos PRESENTES en el formulario actual, y
    // las comparaciones normalizan los dos lados. Un campo que no está (el
    // tipo y los puntos no existen hasta elegir norma) no es "un cambio";
    // tratarlo como cambio rearma el formulario en cada interacción, en bucle.
    if ("numeroEnsayo" in campos && (campos.numeroEnsayo ?? null) !== numeroEnsayoSel) {
      setNumeroEnsayoSel(campos.numeroEnsayo ?? null);
    }
    if ("notasEnsayo" in campos && (campos.notasEnsayo ?? "") !== notasEnsayoTexto) {
      setNotasEnsayoTexto(campos.notasEnsayo ?? "");
    }
    if ("notasEtiqueta" in campos && (campos.notasEtiqueta ?? "") !== notasEtiquetaTexto) {
      setNotasEtiquetaTexto(campos.notasEtiqueta ?? "");
    }
    if ("actividadId" in campos && (campos.actividadId ?? null) !== (actividadSeleccionada ?? null)) {
      const opcion = actividadesOpciones.find((o) => o.value === campos.actividadId);
      setActividadSeleccionada(campos.actividadId ?? null);
      setNormaSeleccionada(null);
      setPuntosNorma([]);
      setPuntosSeleccionados([]);
      setNormasOpciones([]);
      if (opcion?.procesoid) {
        cargarNormas(opcion.procesoid);
      } else {
        setFormEnsayoKey((k) => k + 1);
      }
      return;
    }

    if ("normaId" in campos && (campos.normaId ?? null) !== (normaSeleccionada ?? null)) {
      setNormaSeleccionada(campos.normaId ?? null);
      setPuntosNorma([]);
      setPuntosSeleccionados([]);
      if (!campos.normaId) {
        setFormEnsayoKey((k) => k + 1);
        return;
      }
      setLoaderPuntos(true);
      accessAPI(
        "GET",
        `admin/ensayo/puntos?normaid=${campos.normaId}`,
        null,
        (resp) => {
          setPuntosNorma(resp);
          setPuntosSeleccionados(seleccionInicialPorTipo(tipoEnsayoSel, resp));
          setLoaderPuntos(false);
          setFormEnsayoKey((k) => k + 1);
        },
        (resp) => {
          setLoaderPuntos(false);
          setFormEnsayoKey((k) => k + 1);
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: resp[0]?.msg || "Error al cargar los puntos de ensayo",
            sticky: true,
          });
        },
      );
      return;
    }

    // Un checkbox tocado a mano. Se resuelve ANTES que la rama del tipo:
    // cuando lo que cambió es un punto, el tipo viene igual que el estado y
    // esa rama no dispara; y al revés, cuando cambia el tipo los checkboxes
    // vienen sin tocar.
    const clavesPunto = Object.keys(campos).filter((k) => k.startsWith("punto::"));
    if (clavesPunto.length > 0) {
      const nuevaSeleccion = puntosNorma
        .map((pt) => pt.texto)
        .filter((t) => campos[`punto::${t}`] === true);
      if (nuevaSeleccion.join("\u0000") !== puntosSeleccionados.join("\u0000")) {
        setPuntosSeleccionados(nuevaSeleccion);
        // Modificar la selección de Completo o Seguimiento la convierte en
        // una selección a medida: el tipo pasa solo a Específico. El rearmado
        // es para que el dropdown lo muestre; el checkbox ya se actualizó
        // visualmente solo.
        if (tipoEnsayoSel !== "ESPECIFICO") {
          setTipoEnsayoSel("ESPECIFICO");
          setFormEnsayoKey((k) => k + 1);
        }
        return;
      }
    }

    if (
      "tipoEnsayo" in campos &&
      campos.tipoEnsayo &&
      campos.tipoEnsayo !== tipoEnsayoSel
    ) {
      setTipoEnsayoSel(campos.tipoEnsayo);
      setPuntosSeleccionados(seleccionInicialPorTipo(campos.tipoEnsayo, puntosNorma));
      setFormEnsayoKey((k) => k + 1);
    }
  }

  function cargarNormas(procesoId) {
    setLoaderNormas(true);
    accessAPI(
      "GET",
      `admin/ensayo/normas?procesoid=${procesoId}`,
      null,
      (resp) => {
        setNormasOpciones(resp);
        setLoaderNormas(false);
        setFormEnsayoKey((k) => k + 1);
      },
      (resp) => {
        setLoaderNormas(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al cargar las normas",
          sticky: true,
        });
      },
    );
  }

  function crearEnsayo(campos) {
    const actividadId = campos.actividadId ?? actividadSeleccionada;
    const normaId = campos.normaId ?? normaSeleccionada;
    const hayCheckboxes = Object.keys(campos).some((k) => k.startsWith("punto::"));
    const puntos = hayCheckboxes
      ? puntosNorma.map((pt) => pt.texto).filter((t) => campos[`punto::${t}`] === true)
      : puntosSeleccionados;
    if (!actividadId || !normaId) return;

    const esEdicion = !!ensayoEditandoId;
    const numeroEnsayo = campos.numeroEnsayo ?? numeroEnsayoSel;
    if (!esEdicion && !numeroEnsayo) return;

    const guardarError = (resp) => {
      setGuardandoEnsayo(false);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: resp[0]?.msg || "Error al guardar el ensayo",
        sticky: true,
      });
    };

    const guardarEnAPI = () => {
      accessAPI(
        esEdicion ? "PUT" : "POST",
        esEdicion ? `admin/ensayo/${ensayoEditandoId}` : "admin/ensayo",
        {
          ...(esEdicion ? {} : { numeroEnsayo: parseInt(numeroEnsayo) }),
          muestraid: parseInt(muestraId),
          actividadid: actividadId,
          normaid: normaId,
          puntosSeleccionados: puntosNorma.filter((p) => puntos.includes(p.texto)),
          tipoEnsayo: campos.tipoEnsayo || tipoEnsayoSel,
          notasEnsayo: campos.notasEnsayo || null,
          notasEtiqueta: campos.notasEtiqueta || null,
        },
        () => {
          setGuardandoEnsayo(false);
          setSidebarEnsayo(false);
          cargarEnsayos();
          toast.current?.show({
            severity: "success",
            summary: esEdicion ? "Ensayo actualizado" : "Ensayo registrado",
            life: 3000,
          });
        },
        guardarError,
      );
    };

    setGuardandoEnsayo(true);
    if (esEdicion) {
      guardarEnAPI();
      return;
    }

    // Alta: primero se verifica que el número tipeado corresponda a un ensayo
    // del lote de etiquetas y que siga vacío (mismo patrón que el ingreso de
    // muestra por número).
    accessAPI(
      "GET",
      `admin/ensayo/numero/${parseInt(numeroEnsayo)}`,
      null,
      (ensayoImpreso) => {
        if (ensayoImpreso.estado !== "PENDIENTE") {
          setGuardandoEnsayo(false);
          toast.current?.show({
            severity: "error",
            summary: "Ensayo ya registrado",
            detail: `El ensayo #${numeroEnsayo} ya está registrado${
              ensayoImpreso.muestra?.numero
                ? ` en la muestra ${ensayoImpreso.muestra.numero}`
                : ""
            }. Verificá el número de la etiqueta.`,
            sticky: true,
          });
          return;
        }
        guardarEnAPI();
      },
      (resp) => {
        setGuardandoEnsayo(false);
        toast.current?.show({
          severity: "error",
          summary: "Ensayo no encontrado",
          detail:
            resp?.[0]?.msg ||
            `No existe un ensayo con número ${numeroEnsayo}. Generá sus etiquetas primero.`,
          sticky: true,
        });
      },
    );
  }

  // "Editar" del kebab: abre el MISMO sidebar en modo edición, precargando
  // proceso → normas → puntos y el snapshot guardado del ensayo. Guarda con PUT.
  function abrirSidebarEnsayoEdicion(ens) {
    const err = (resp) =>
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: resp?.[0]?.msg || "Error al cargar el ensayo",
        sticky: true,
      });

    setEnsayoEditandoId(ens.id);
    setSidebarEnsayo(true);
    setNormasOpciones([]);
    setPuntosNorma([]);
    setPuntosSeleccionados([]);

    accessAPI(
      "GET",
      `admin/ensayo/${ens.id}`,
      null,
      (detalle) => {
        setTipoEnsayoSel(detalle.tipoEnsayo || "SEGUIMIENTO");
        setNotasEnsayoTexto(detalle.notasEnsayo || "");
        setNotasEtiquetaTexto(detalle.notasEtiqueta || "");
        const procId = detalle.proceso?.id || null;
        const normId = detalle.norma?.id || null;
        const textosSel = (detalle.puntosSeleccionados || []).map((p) => p.texto);

        setActividadesOpciones(actividadesDisponibles(detalle.actividad));
        setActividadSeleccionada(detalle.actividad?.id ?? null);
        setFormEnsayoKey((k) => k + 1);
        if (!procId) return;
        accessAPI(
          "GET",
          `admin/ensayo/normas?procesoid=${procId}`,
          null,
          (normas) => {
            setNormasOpciones(normas);
            setNormaSeleccionada(normId);
            if (!normId) return;
            setLoaderPuntos(true);
            accessAPI(
              "GET",
              `admin/ensayo/puntos?normaid=${normId}`,
              null,
              (puntos) => {
                setPuntosNorma(puntos);
                setPuntosSeleccionados(textosSel);
                setLoaderPuntos(false);
                setFormEnsayoKey((k) => k + 1);
              },
              (r) => {
                setLoaderPuntos(false);
                err(r);
              },
            );
          },
          err,
        );
      },
      err,
    );
  }

  function abrirMenuEnsayo(ev, ens) {
    setMenuAccionesItems([
      {
        label: "Editar",
        icon: "pi pi-pencil",
        command: () => abrirSidebarEnsayoEdicion(ens),
      },
      {
        label: "Imprimir etiqueta",
        icon: "pi pi-print",
        command: () => imprimirEtiquetaEnsayo(ens.id, ens.numero),
      },
      ...(ens.fechaResultado
        ? []
        : [
            {
              label: "Registrar resultado",
              icon: "pi pi-check",
              command: () => registrarResultado(ens.id),
            },
          ]),
      { separator: true },
      {
        label: "Eliminar",
        icon: "pi pi-trash",
        command: () => eliminarEnsayo(ens),
      },
    ]);
    menuAccionesRef.current?.toggle(ev);
  }

  function eliminarEnsayo(ens) {
    confirmDialog({
      message: `¿Eliminar el ensayo ${ens.numero}? La etiqueta impresa queda libre para registrarla en otra muestra.`,
      header: "Eliminar ensayo",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sí, eliminar",
      rejectLabel: "No",
      acceptClassName: "p-button-danger",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/ensayo/${ens.id}`,
          null,
          () => {
            cargarEnsayos();
          },
          (resp) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: resp[0]?.msg || "Error al eliminar el ensayo",
              sticky: true,
            });
          },
        );
      },
    });
  }

  function registrarResultado(ensayoId) {
    confirmDialog({
      message:
        "¿Registrar que el laboratorio entregó el resultado de este ensayo? Esto no repone la muestra al inventario.",
      header: "Registrar resultado",
      acceptLabel: "Sí",
      rejectLabel: "No",
      accept: () => {
        accessAPI(
          "POST",
          `admin/ensayo/${ensayoId}/resultado`,
          null,
          () => {
            cargarEnsayos();
          },
          (resp) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: resp[0]?.msg || "Error al registrar el resultado",
              sticky: true,
            });
          },
        );
      },
    });
  }

  function imprimirEtiquetaEnsayo(ensayoId, numeroEnsayo) {
    setImprimiendoEnsayoId(ensayoId);
    downloadFile(
      `admin/ensayo/${ensayoId}/etiqueta.pdf`,
      `etiqueta-ensayo-${numeroEnsayo}.pdf`,
      () => setImprimiendoEnsayoId(null),
      (resp) => {
        setImprimiendoEnsayoId(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: resp[0]?.msg || "Error al imprimir la etiqueta",
          sticky: true,
        });
      },
    );
  }

  const editarConfig = muestra
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "procesoid",
                tipoitemformularioid: 5,
                etiqueta: "Proceso",
                opciones: "admin/muestra/procesos",
                respuesta: muestra.producto?.proceso?.id,
                requerido: true,
                sinOpcionInicial: true,
              },
              {
                nombre: "productoid",
                tipoitemformularioid: 5,
                etiqueta: "Producto (opcional)",
                opciones: "admin/muestra/productos",
                queryparams: ["procesoid"],
                respuesta: muestra.productoid,
                sinOpcionInicial: true,
              },
              {
                nombre: "actividadid",
                tipoitemformularioid: 5,
                etiqueta: "Actividad (opcional)",
                opciones: "admin/muestra/actividades",
                queryparams: ["procesoid"],
                respuesta: muestra.actividadid,
                sinOpcionInicial: true,
              },
              {
                nombre: "cajaId",
                tipoitemformularioid: 5,
                etiqueta: "Caja",
                opciones: "admin/caja/dropdown",
                respuesta: muestra.cajaId,
                sinOpcionInicial: true,
              },
              {
                nombre: "fechaadquisicion",
                tipoitemformularioid: 7,
                etiqueta: "Fecha de adquisición",
                respuesta: muestra.fechaadquisicion
                  ? new Date(muestra.fechaadquisicion)
                  : null,
              },
              {
                nombre: "notas",
                tipoitemformularioid: 11,
                etiqueta: "Notas",
                respuesta: muestra.notas ?? "",
              },
            ],
          },
        ],
        botones: [
          {
            etiqueta:
              muestra.estado === "PENDIENTE" ? "Ingresar muestra" : "Guardar",
            callback: guardar,
            validar: true,
          },
        ],
      }
    : null;

  if (!muestra) {
    return (
      <>
        <Skeleton width="10rem" height="2.5rem" css={{ marginTop: "2em" }} />
        <Skeleton width="100%" height="10rem" css={{ marginTop: "1em" }} />
      </>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu popup ref={menuAccionesRef} model={menuAccionesItems} />
      <Titulo
        urlVolver="/muestra"
        etiquetas={
          muestra.desactivada
            ? [{ texto: "Desactivada", error: true }]
            : muestra.retirada
              ? [{ texto: "Retirada de la caja", alerta: true }]
              : muestra.estado === "INGRESADA"
                ? [{ texto: "Ingresada", ok: true }]
                : [{ texto: "Pendiente de ingreso", neutro: true }]
        }
        botones={
          <div css={{ display: "flex", gap: "0.5rem" }}>
            {!muestra.desactivada &&
              muestra.estado === "INGRESADA" &&
              !muestra.retirada && (
                <Boton callback={retirar} loader={loaderRetiro} transparente>
                  Retirar
                </Boton>
              )}
            {!muestra.desactivada && muestra.retirada && (
              <Boton callback={devolver} loader={loaderRetiro} transparente>
                Devolver
              </Boton>
            )}
            {muestra.desactivada ? (
              <Boton callback={activar} loader={loaderDesactivar} verde>
                Activar
              </Boton>
            ) : muestra.estado === "INGRESADA" ? (
              <Boton callback={desactivar} loader={loaderDesactivar} rojo>
                Desactivar
              </Boton>
            ) : null}
          </div>
        }
      >
        Muestra {muestra.numero}
      </Titulo>

      <div
        css={{
          display: "flex",
          gap: "1em",
          alignItems: "flex-start",
        }}
      >
        <div css={{ flex: "1 1 auto", width: "100%", minWidth: 0 }}>
        <Card>
          <Titulo
            botones={
              <Boton icono negro callback={() => setSidebarEditar(true)}>
                <i className="pi pi-pencil" />
              </Boton>
            }
          >
            Información general
          </Titulo>
        <div className="grid">
          <CampoInformacion nombreCampo="Cliente">
            {muestra.producto?.proceso?.cliente?.razonsocial || "—"}
          </CampoInformacion>
          <CampoInformacion nombreCampo="Proceso">
            {muestra.producto?.proceso
              ? `${muestra.producto.proceso.nombre || ""}${muestra.producto.proceso.nombre && muestra.producto.proceso.nombrefantasia ? " - " : ""}${muestra.producto.proceso.nombrefantasia || ""}` || "—"
              : "—"}
          </CampoInformacion>
          <CampoInformacion nombreCampo="Producto">
            {muestra.producto
              ? muestra.productoEtiqueta || `#${muestra.producto.id}`
              : "—"}
          </CampoInformacion>
          <CampoInformacion nombreCampo="Actividad">
            {muestra.actividad?.actividadtipo?.nombre
              ? `${muestra.actividad.actividadtipo.nombre}${muestra.actividad.fechacreacion ? ` - ${formatFecha(muestra.actividad.fechacreacion)}` : ""}`
              : "—"}
          </CampoInformacion>
          <CampoInformacion nombreCampo="Caja">
            {muestra.caja
              ? `${muestra.caja.numero}${muestra.caja.descripcion ? ` - ${muestra.caja.descripcion}` : ""}`
              : "—"}
          </CampoInformacion>
          <CampoInformacion nombreCampo="Fecha de adquisición">
            {muestra.fechaadquisicion ? formatFecha(muestra.fechaadquisicion) : "—"}
          </CampoInformacion>
          {muestra.retirada && (
            <CampoInformacion nombreCampo="Retirada por">
              {muestra.retiradaAdministrador
                ? `${muestra.retiradaAdministrador.nombre} ${muestra.retiradaAdministrador.apellido}`
                : "—"}
              {muestra.fecharetiro && ` — ${formatFecha(muestra.fecharetiro)}`}
            </CampoInformacion>
          )}
          {muestra.desactivada && (
            <CampoInformacion nombreCampo="Desactivada por">
              {muestra.desactivadaAdministrador
                ? `${muestra.desactivadaAdministrador.nombre} ${muestra.desactivadaAdministrador.apellido}`
                : "—"}
              {muestra.fechadesactivacion &&
                ` — ${formatFecha(muestra.fechadesactivacion)}`}
            </CampoInformacion>
          )}
          <CampoInformacion nombreCampo="Notas" anchoCompleto>
            {muestra.notas || "—"}
          </CampoInformacion>
        </div>
        </Card>
        {muestra.estado === "INGRESADA" && (
        <Card css={{ marginTop: "1.5rem" }}>
        <Titulo>Comprobante de compra</Titulo>
        {muestra.comprobanteArchivo ? (
          <div css={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <ArchivoPreview
              hash={muestra.comprobanteArchivo.hash}
              nombre={muestra.comprobanteArchivo.nombredescarga}
            />
            <Boton callback={eliminarComprobante} rojo chico texto>
              Eliminar
            </Boton>
          </div>
        ) : (
          <SubidorArchivos
            presignEndpoint="admin/archivo/presign"
            confirmarEndpoint={`admin/muestra/${muestraId}/comprobante/confirmar`}
            ubicacion="muestrascomprobantes"
            chooseLabel="Subir comprobante"
            onSubido={() => cargar()}
          />
        )}
        </Card>
        )}
        </div>
        {esXl && (
          <div css={{ flex: "0 0 320px" }}>
            <PdfInline
              endpoint={`admin/muestra/${muestraId}/etiqueta.pdf`}
              height="auto"
              sinMarco
            />
          </div>
        )}
      </div>

      {muestra.estado === "INGRESADA" && (
        <>
      <Card css={{ marginTop: "1.5rem" }}>
        <Titulo>Imágenes</Titulo>
        <div css={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <SubidorArchivos
            presignEndpoint="admin/archivo/presign"
            confirmarEndpoint={`admin/muestra/${muestraId}/imagenes/confirmar`}
            ubicacion="muestrasimagenes"
            multiple
            accept="image/*"
            chooseLabel="Agregar imágenes"
            onSubido={() => cargar()}
          />
          <Boton callback={abrirDialogQR} transparente>
            Subir imágenes desde otro dispositivo
          </Boton>
        </div>
        {muestra.imagenes?.length > 0 && (
          <div
            css={{
              marginTop: "1rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {muestra.imagenes.map((img) => (
              <div
                key={img.id}
                css={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Image
                  src={`${API_URL}/archivo/${img.archivo.hash}/preview`}
                  alt={img.archivo.nombredescarga}
                  preview
                  imageStyle={{
                    width: "160px",
                    height: "120px",
                    objectFit: "cover",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                  }}
                />
                <Boton
                  callback={() => eliminarImagen(img.id)}
                  rojo
                  chico
                  texto
                >
                  Eliminar
                </Boton>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card css={{ marginTop: "1.5rem" }}>
        <Titulo
          botones={
            <Boton icono negro callback={abrirCandidatos}>
              <i className="pi pi-plus" />
            </Boton>
          }
        >
          Procesos adicionales
        </Titulo>
        <TablaFiltrable conFiltros={false}
          value={muestra.muestra_x_proceso || []}
          dataKey="id"
          stripedRows
          emptyMessage="Todavía no hay procesos vinculados a esta muestra."
          nombreArchivo="procesos_adicionales_muestra"
        >
          <Column
            header="Proceso"
            body={(v) => (
              <span
                onClick={() => v.proceso && navigate(`/proceso/${v.procesoid}`)}
                css={{
                  cursor: v.proceso ? "pointer" : "default",
                  "&:hover": v.proceso ? { color: "var(--principal)" } : {},
                }}
              >
                {v.proceso
                  ? `${v.proceso.nombre || ""}${
                      v.proceso.nombre && v.proceso.nombrefantasia ? " - " : ""
                    }${v.proceso.nombrefantasia || ""}` || `#${v.proceso.id}`
                  : "—"}
              </span>
            )}
          />
          <Column
            header="Actividad"
            body={(v) =>
              v.actividad
                ? `${v.actividad.actividadtipo?.nombre || "Actividad"} — ${
                    v.actividad.fechacreacion ? formatFecha(v.actividad.fechacreacion) : ""
                  }`
                : "—"
            }
          />
          <Column
            header="Vinculado el"
            body={(v) => (v.fechaVinculacion ? formatFecha(v.fechaVinculacion) : "—")}
          />
          <Column
            body={(v) => (
              <Boton
                icono
                rojo
                chico
                confirmacion="¿Desvincular este proceso de la muestra?"
                callback={() => desvincular(v.procesoid)}
                loader={desvinculandoId === v.procesoid}
              >
                <i className="pi pi-trash" />
              </Boton>
            )}
            style={{ width: "3rem", textAlign: "center" }}
          />
        </TablaFiltrable>
      </Card>

      <Card css={{ marginTop: "1.5rem" }}>
        <Titulo
          botones={
            <Boton icono negro callback={abrirSidebarEnsayo}>
              <i className="pi pi-plus" />
            </Boton>
          }
        >
          Ensayos
        </Titulo>
        <TablaFiltrable conFiltros={false}
          value={ensayos || []}
          dataKey="id"
          stripedRows
          loading={!ensayos}
          emptyMessage="Todavía no se envió esta muestra a ningún ensayo."
          nombreArchivo="ensayos_muestra"
        >
          <Column header="Número" body={(e) => e.numero} />
          <Column header="Clave" body={(e) => e.claveSeguridad || "—"} />
          {!esMobile && (
            <Column header="Norma" body={(e) => e.norma?.numero || "—"} />
          )}
          <Column
            header="Enviado"
            body={(e) => (e.fechaCreacion ? formatFecha(e.fechaCreacion) : "—")}
          />
          {!esMobile && (
            <Column
              header="Tipo"
              body={(e) =>
                ({ COMPLETO: "Completo", SEGUIMIENTO: "Seguimiento", ESPECIFICO: "Específico" }[
                  e.tipoEnsayo
                ] || "—")
              }
            />
          )}
          {esMobile ? (
            // En mobile no entra la fecha: solo un check cuando el ensayo terminó.
            <Column
              header=""
              body={(e) =>
                e.fechaResultado ? (
                  <i className="pi pi-check" css={{ color: "var(--verde)" }} />
                ) : null
              }
              style={{ width: "3rem", textAlign: "center" }}
            />
          ) : (
            <Column
              header="Resultado entregado"
              body={(e) => (e.fechaResultado ? formatFecha(e.fechaResultado) : "—")}
            />
          )}
          <Column
            body={(e) => (
              <Boton
                icono
                negro
                chico
                loader={imprimiendoEnsayoId === e.id}
                callback={(ev) => abrirMenuEnsayo(ev, e)}
              >
                <i className="pi pi-ellipsis-v" />
              </Boton>
            )}
            style={{ width: "3rem", textAlign: "center" }}
          />
        </TablaFiltrable>
      </Card>
        </>
      )}

      <Dialog
        header="Subir imágenes desde otro dispositivo"
        visible={dialogQRVisible}
        onHide={() => {
          setDialogQRVisible(false);
          // Recargar la muestra al cerrar el popup para traer las imágenes
          // recién subidas desde el otro dispositivo.
          cargar();
        }}
        style={{ width: "min(420px, 90vw)" }}
      >
        {loaderQR && <p>Generando…</p>}
        {!loaderQR && uploadInfo && (
          <div css={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
            <p css={{ margin: 0, textAlign: "center" }}>
              Escaneá este QR desde el celular para abrir la página de subida
              de imágenes de esta muestra.
            </p>
            <img
              src={uploadInfo.qr}
              alt="QR de subida"
              css={{ width: "240px", height: "240px" }}
            />
            <a
              href={uploadInfo.url}
              target="_blank"
              rel="noreferrer"
              css={{
                wordBreak: "break-all",
                fontSize: "0.8em",
                textAlign: "center",
              }}
            >
              {uploadInfo.url}
            </a>
          </div>
        )}
      </Dialog>

      <Sidebar
        visible={sidebarEditar}
        onHide={() => setSidebarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <h2>
          {muestra.estado === "PENDIENTE" ? "Ingresar muestra" : "Editar muestra"}
        </h2>
        {editarConfig && <Formulario config={editarConfig} loader={loaderEditar} />}
      </Sidebar>

      <Sidebar
        visible={sidebarCandidatos}
        onHide={() => setSidebarCandidatos(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Vincular a un proceso</Titulo>
        {loaderCandidatos && <p>Buscando procesos candidatos…</p>}
        {!loaderCandidatos && candidatos?.length === 0 && (
          <Message
            severity="warn"
            css={{ width: "100%" }}
            text="No se encontraron procesos candidatos: no hay otro proceso activo del mismo cliente cuyo producto tenga exactamente los mismos valores identificatorios."
          />
        )}
        {!loaderCandidatos && candidatos?.length > 0 && (
          <>
            <Formulario
              key={formVincularKey}
              loader={vinculando}
              onCambio={cambioFormVincular}
              config={{
                secciones: [
                  {
                    campos: [
                      {
                        nombre: "procesoId",
                        tipoitemformularioid: 5,
                        etiqueta: "Proceso",
                        requerido: true,
                        opciones: candidatos.map((c) => ({
                          value: c.procesoId,
                          etiqueta:
                            `${c.nombre || ""}${c.nombre && c.nombrefantasia ? " - " : ""}${c.nombrefantasia || ""}` ||
                            `#${c.procesoId}`,
                        })),
                        respuesta: procesoBorrador,
                        sinOpcionInicial: true,
                      },
                      {
                        nombre: "actividadId",
                        tipoitemformularioid: 5,
                        etiqueta: "Actividad (opcional)",
                        opciones: actividadesCandidato,
                        sinOpcionInicial: true,
                      },
                    ],
                  },
                ],
                botones: [{ etiqueta: "Vincular", callback: vincular, validar: true }],
              }}
            />
          </>
        )}
      </Sidebar>

      <Sidebar
        visible={sidebarEnsayo}
        onHide={() => setSidebarEnsayo(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>{ensayoEditandoId ? "Editar ensayo" : "Registrar ensayo"}</Titulo>
        <Formulario
          key={formEnsayoKey}
          loader={guardandoEnsayo || loaderNormas || loaderPuntos}
          onCambio={cambioFormEnsayo}
          config={{
            secciones: [
              {
                campos: [
                  // El número sale de la etiqueta impresa y solo se tipea en
                  // el alta: al editar no cambia (la etiqueta sigue válida).
                  ...(ensayoEditandoId
                    ? []
                    : [
                        {
                          nombre: "numeroEnsayo",
                          tipoitemformularioid: 4,
                          etiqueta: "Número de ensayo (de la etiqueta impresa)",
                          requerido: true,
                          respuesta: numeroEnsayoSel,
                        },
                      ]),
                  {
                    nombre: "actividadId",
                    tipoitemformularioid: 5,
                    etiqueta: "Actividad",
                    requerido: true,
                    opciones: actividadesOpciones,
                    respuesta: actividadSeleccionada,
                    sinOpcionInicial: true,
                  },
                  {
                    nombre: "normaId",
                    tipoitemformularioid: 5,
                    etiqueta: "Norma",
                    requerido: true,
                    opciones: normasOpciones,
                    respuesta: normaSeleccionada,
                    disabled: !actividadSeleccionada,
                    sinOpcionInicial: true,
                  },
                  ...(normaSeleccionada
                    ? [
                        {
                          nombre: "tipoEnsayo",
                          tipoitemformularioid: 5,
                          etiqueta: "Tipo de ensayo",
                          opciones: TIPOS_ENSAYO,
                          respuesta: tipoEnsayoSel,
                          // Sin esto, Formulario toma la primera opción como
                          // contenido y NI SIQUIERA dibuja el campo.
                          sinOpcionInicial: true,
                        },
                        // Todos los puntos de la norma, siempre, como
                        // checkboxes. El tipo de ensayo los marca según su
                        // criterio; tocar uno a mano vuelve el tipo a
                        // Específico (ver cambioFormEnsayo).
                        ...puntosNorma.map((p) => ({
                          nombre: `punto::${p.texto}`,
                          tipoitemformularioid: 6,
                          etiqueta: p.texto,
                          respuesta: puntosSeleccionados.includes(p.texto),
                        })),
                      ]
                    : []),
                  {
                    nombre: "notasEnsayo",
                    tipoitemformularioid: 11,
                    etiqueta: "Notas para el ensayo",
                    respuesta: notasEnsayoTexto,
                  },
                  {
                    nombre: "notasEtiqueta",
                    tipoitemformularioid: 11,
                    etiqueta: "Notas para la etiqueta",
                    respuesta: notasEtiquetaTexto,
                  },
                ],
              },
            ],
            botones: [
              {
                etiqueta: ensayoEditandoId ? "Guardar cambios" : "Registrar ensayo",
                callback: crearEnsayo,
                validar: true,
              },
            ],
          }}
        />
      </Sidebar>
    </>
  );
}
